import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, apiKeysApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  UserCircleIcon,
  BellIcon,
  KeyIcon,
  PaintBrushIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  ScaleIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { useUnitsStore } from '../store/unitsStore';
import type { UnitSystem } from '../utils/units';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/common/ConfirmModal';
import WebPushSection from '../components/settings/WebPushSection';
import TakDestinationsSection from '../components/settings/TakDestinationsSection';
// Phase 10: Dedrone integrations moved to the Sensors page so the operator
// can manage multiple DroneTracker servers alongside the sub-sensors each
// one discovers. The DedroneIntegrationSection component is now unused.

// ============================================================================
// Tab definitions
// ============================================================================

const tabs = [
  { id: 'profile', label: 'Profile', icon: UserCircleIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
  { id: 'api-keys', label: 'API Keys', icon: KeyIcon },
  { id: 'tak', label: 'TAK', icon: ShareIcon },
  { id: 'units', label: 'Units', icon: ScaleIcon },
  { id: 'appearance', label: 'Appearance', icon: PaintBrushIcon },
] as const;

type TabId = (typeof tabs)[number]['id'];

// ============================================================================
// Profile Tab
// ============================================================================

function ProfileTab() {
  const user = useAuthStore((s) => s.user);
  const { register, handleSubmit, formState: { isDirty } } = useForm({
    defaultValues: { firstName: user?.firstName || '', lastName: user?.lastName || '', email: user?.email || '' },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string }) => authApi.updateMe(data),
    onSuccess: () => toast.success('Profile updated'),
    onError: () => toast.error('Failed to update profile'),
  });

  const {
    register: registerPw,
    handleSubmit: handlePwSubmit,
    reset: resetPw,
    formState: { isDirty: isPwDirty },
  } = useForm({ defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' } });

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data.currentPassword, data.newPassword),
    onSuccess: () => { toast.success('Password changed'); resetPw(); },
    onError: () => toast.error('Failed to change password'),
  });

  return (
    <div className="space-y-8">
      {/* Profile info */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Personal Information</h3>
        <p className="text-sm text-gray-500 mb-6">Update your name and contact details.</p>
        <form onSubmit={handleSubmit((data) => updateMutation.mutate(data))} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">First Name</label>
              <input {...register('firstName')} className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Last Name</label>
              <input {...register('lastName')} className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Email Address</label>
            <input {...register('email')} disabled className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-gray-500 text-sm cursor-not-allowed" />
            <p className="text-[11px] text-gray-600 mt-1">Contact an administrator to change your email.</p>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={!isDirty || updateMutation.isPending}
              className="btn-primary px-5 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Change Password</h3>
        <p className="text-sm text-gray-500 mb-6">Ensure your account uses a strong, unique password.</p>
        <form onSubmit={handlePwSubmit((data) => {
          if (data.newPassword !== data.confirmPassword) { toast.error('Passwords do not match'); return; }
          passwordMutation.mutate({ currentPassword: data.currentPassword, newPassword: data.newPassword });
        })} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Current Password</label>
            <input type="password" {...registerPw('currentPassword', { required: true })}
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">New Password</label>
            <input type="password" {...registerPw('newPassword', { required: true, minLength: 8 })}
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Confirm New Password</label>
            <input type="password" {...registerPw('confirmPassword', { required: true })}
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={!isPwDirty || passwordMutation.isPending}
              className="btn-primary px-5 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              {passwordMutation.isPending ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Notifications Tab
// ============================================================================

function NotificationsTab() {
  const [prefs, setPrefs] = useState({
    emailAlerts: true,
    inAppNotifications: true,
    criticalOnly: false,
    dailyDigest: false,
    webhookEnabled: false,
    webhookUrl: '',
  });

  const toggle = (key: keyof typeof prefs) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const saveMutation = useMutation({
    mutationFn: () => authApi.updateMe({ preferences: { notifications: prefs } }),
    onSuccess: () => toast.success('Notification preferences saved'),
    onError: () => toast.error('Failed to save preferences'),
  });

  return (
    <div className="space-y-8">
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Alert Preferences</h3>
        <p className="text-sm text-gray-500 mb-6">Configure how you receive alerts and notifications.</p>
        <div className="space-y-5">
          {[
            { key: 'emailAlerts' as const, label: 'Email Alerts', desc: 'Receive alerts via email for detection events' },
            { key: 'inAppNotifications' as const, label: 'In-App Notifications', desc: 'Show real-time notifications in the application' },
            { key: 'criticalOnly' as const, label: 'Critical Alerts Only', desc: 'Only receive notifications for critical-severity events' },
            { key: 'dailyDigest' as const, label: 'Daily Digest', desc: 'Receive a daily summary email of all detection activity' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-white">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
              <button onClick={() => toggle(item.key)}
                className={clsx('relative w-11 h-6 rounded-full transition-colors', prefs[item.key] ? 'bg-accent' : 'bg-navy-600')}>
                <span className={clsx('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform', prefs[item.key] && 'translate-x-5')} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Browser push notifications — VAPID-based web-push, live on the device.
          Kept between coarse "Alert Preferences" (email/digest) and the
          "Webhook Integration" card so users discover per-fence rules in the
          same place they opt in to push delivery. */}
      <WebPushSection />

      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Webhook Integration</h3>
        <p className="text-sm text-gray-500 mb-6">Forward alerts to an external endpoint for custom integrations.</p>
        <div className="flex items-center justify-between py-2 mb-4">
          <div>
            <p className="text-sm font-medium text-white">Enable Webhook</p>
            <p className="text-xs text-gray-500">POST alert payloads to your endpoint</p>
          </div>
          <button onClick={() => toggle('webhookEnabled')}
            className={clsx('relative w-11 h-6 rounded-full transition-colors', prefs.webhookEnabled ? 'bg-accent' : 'bg-navy-600')}>
            <span className={clsx('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform', prefs.webhookEnabled && 'translate-x-5')} />
          </button>
        </div>
        {prefs.webhookEnabled && (
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Webhook URL</label>
            <input type="url" value={prefs.webhookUrl} onChange={(e) => setPrefs((p) => ({ ...p, webhookUrl: e.target.value }))}
              placeholder="https://your-endpoint.com/webhook"
              className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
        )}
        <div className="flex justify-end mt-6">
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="btn-primary px-5 py-2 text-sm disabled:opacity-40">
            {saveMutation.isPending ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// API Keys Tab
// ============================================================================

function ApiKeysTab() {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());

  const { data: keysData, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: () => apiKeysApi.create({ name: newKeyName, permissions: ['read'] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key created');
      setShowCreateForm(false);
      setNewKeyName('');
    },
    onError: () => toast.error('Failed to create API key'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key revoked');
    },
    onError: () => toast.error('Failed to revoke API key'),
  });

  const keys = keysData?.data || [];

  const copyKey = (key: string, id: string) => {
    navigator.clipboard.writeText(key);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleReveal = (id: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">API Keys</h3>
            <p className="text-sm text-gray-500">Manage keys for programmatic access to the ASNE C2 API.</p>
          </div>
          <button onClick={() => setShowCreateForm(true)} className="btn-primary flex items-center gap-2 text-sm">
            <PlusIcon className="h-4 w-4" />New Key
          </button>
        </div>

        {showCreateForm && (
          <div className="mb-6 p-4 bg-navy-800 border border-navy-700 rounded-lg">
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Key Name</label>
            <div className="flex gap-3">
              <input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="e.g., Production Backend"
                className="flex-1 px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
              <button onClick={() => createMutation.mutate()} disabled={!newKeyName.trim() || createMutation.isPending}
                className="btn-primary px-4 py-2 text-sm disabled:opacity-40">Create</button>
              <button onClick={() => { setShowCreateForm(false); setNewKeyName(''); }}
                className="btn-secondary px-4 py-2 text-sm">Cancel</button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 skeleton rounded-lg" />
            ))}
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-10">
            <KeyIcon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No API keys yet.</p>
            <p className="text-xs text-gray-600">Create a key to get started with the API.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {keys.map((key: any) => (
              <div key={key.id} className="flex items-center justify-between p-4 bg-navy-800 border border-navy-700 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">{key.name}</span>
                    <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-semibold',
                      key.isActive !== false ? 'bg-accent/10 text-accent' : 'bg-gray-500/10 text-gray-500')}>
                      {key.isActive !== false ? 'Active' : 'Revoked'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs text-gray-500 font-mono">
                      {revealedKeys.has(key.id) ? (key.key || key.prefix + '...') : (key.prefix ? key.prefix + '••••••••' : '••••••••••••••••')}
                    </code>
                    <button onClick={() => toggleReveal(key.id)} className="text-gray-600 hover:text-gray-400">
                      {revealedKeys.has(key.id) ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => copyKey(key.key || key.prefix || key.id, key.id)} className="text-gray-600 hover:text-gray-400">
                      {copiedId === key.id ? <CheckIcon className="h-3.5 w-3.5 text-accent" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  {key.createdAt && <p className="text-[11px] text-gray-600 mt-1">Created {new Date(key.createdAt).toLocaleDateString()}</p>}
                </div>
                <button onClick={() => setRevokeTarget(key.id)} disabled={key.isActive === false}
                  className="p-2 text-gray-500 hover:text-alert transition-colors rounded-lg hover:bg-alert/10 disabled:opacity-30 disabled:cursor-not-allowed">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal isOpen={!!revokeTarget} onClose={() => setRevokeTarget(null)}
        onConfirm={() => { if (revokeTarget) revokeMutation.mutate(revokeTarget); }}
        title="Revoke API Key" message="Are you sure you want to revoke this API key? Any integrations using this key will stop working immediately."
        confirmLabel="Revoke" variant="danger" />
    </div>
  );
}

// ============================================================================
// Units Tab
// ============================================================================

function UnitsTab() {
  const { unitSystem, setUnitSystem } = useUnitsStore();

  const systems: { id: UnitSystem; label: string; desc: string; examples: string }[] = [
    {
      id: 'imperial',
      label: 'Imperial (US)',
      desc: 'Feet for altitude, mph for speed, miles for distance',
      examples: '400 ft · 25 mph · 1.2 mi',
    },
    {
      id: 'metric',
      label: 'Metric (SI)',
      desc: 'Meters for altitude, m/s for speed, km for distance',
      examples: '122 m · 11.2 m/s · 2.0 km',
    },
  ];

  return (
    <div className="glass-card p-6">
      <h3 className="text-lg font-semibold text-white mb-1">Measurement Units</h3>
      <p className="text-sm text-gray-500 mb-6">
        Choose your preferred unit system. This applies to all altitude, speed,
        and distance displays throughout the application. Stored data is not
        affected — only the display format changes.
      </p>

      <div className="space-y-3">
        {systems.map((sys) => {
          const active = unitSystem === sys.id;
          return (
            <button
              key={sys.id}
              onClick={() => setUnitSystem(sys.id)}
              className={clsx(
                'w-full flex items-start gap-4 p-4 rounded-lg border-2 text-left transition-all',
                active
                  ? 'border-accent bg-accent/5'
                  : 'border-navy-700 bg-navy-800 hover:border-navy-500'
              )}
            >
              {/* Radio indicator */}
              <div
                className={clsx(
                  'mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                  active ? 'border-accent' : 'border-navy-500'
                )}
              >
                {active && (
                  <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className={clsx(
                    'text-sm font-semibold',
                    active ? 'text-accent' : 'text-white'
                  )}
                >
                  {sys.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{sys.desc}</p>
                <p
                  className={clsx(
                    'text-xs font-mono mt-2',
                    active ? 'text-accent/70' : 'text-gray-600'
                  )}
                >
                  {sys.examples}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-gray-600 mt-4">
        This preference is saved to your browser and takes effect immediately.
      </p>
    </div>
  );
}

// ============================================================================
// Appearance Tab
// ============================================================================

function AppearanceTab() {
  return (
    <div className="glass-card p-6">
      <h3 className="text-lg font-semibold text-white mb-1">Appearance</h3>
      <p className="text-sm text-gray-500 mb-6">Customize the look and feel of the application.</p>

      <div className="space-y-6">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-3">Theme</label>
          <div className="flex gap-3">
            <div className="flex-1 p-4 bg-navy-800 border-2 border-accent rounded-lg cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-navy-900 border border-navy-700" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-2 w-3/4 bg-navy-700 rounded" />
                  <div className="h-2 w-1/2 bg-navy-700 rounded" />
                </div>
              </div>
              <p className="text-xs font-medium text-white">Dark (Active)</p>
            </div>
            <div className="flex-1 p-4 bg-navy-800 border border-navy-700 rounded-lg opacity-40 cursor-not-allowed">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gray-200 border border-gray-300" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-2 w-3/4 bg-gray-300 rounded" />
                  <div className="h-2 w-1/2 bg-gray-300 rounded" />
                </div>
              </div>
              <p className="text-xs font-medium text-gray-500">Light (Coming Soon)</p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-3">Accent Color</label>
          <div className="flex gap-3">
            {[
              { color: 'bg-accent', label: 'Teal', active: true },
              { color: 'bg-sky', label: 'Blue', active: false },
              { color: 'bg-purple-500', label: 'Purple', active: false },
              { color: 'bg-amber-500', label: 'Amber', active: false },
            ].map((c) => (
              <button key={c.label} disabled={!c.active}
                className={clsx('flex flex-col items-center gap-1.5 p-3 rounded-lg transition-colors',
                  c.active ? 'bg-navy-800 border-2 border-accent' : 'bg-navy-800 border border-navy-700 opacity-40 cursor-not-allowed')}>
                <div className={clsx('w-8 h-8 rounded-full', c.color)} />
                <span className="text-[11px] text-gray-400">{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-3">Map Provider</label>
          <div className="flex gap-3">
            <div className="flex-1 p-3 bg-navy-800 border-2 border-accent rounded-lg cursor-pointer text-center">
              <p className="text-sm font-medium text-white">CesiumJS</p>
              <p className="text-[11px] text-gray-500">3D Globe (Active)</p>
            </div>
            <div className="flex-1 p-3 bg-navy-800 border border-navy-700 rounded-lg opacity-40 cursor-not-allowed text-center">
              <p className="text-sm font-medium text-gray-500">Mapbox</p>
              <p className="text-[11px] text-gray-600">2D Map (Coming Soon)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Settings Page
// ============================================================================

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      <div className="mb-6">
        <h1 className="page-title">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">Manage your account preferences and configuration</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Vertical tab nav */}
        <nav className="lg:w-56 flex-shrink-0">
          <div className="glass-card p-2 lg:sticky lg:top-6">
            <div className="flex lg:flex-col gap-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={clsx('flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left',
                      activeTab === tab.id
                        ? 'bg-accent/10 text-accent'
                        : 'text-gray-400 hover:text-white hover:bg-navy-800')}>
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="hidden lg:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'api-keys' && <ApiKeysTab />}
          {activeTab === 'tak' && <TakDestinationsSection />}
          {activeTab === 'units' && <UnitsTab />}
          {activeTab === 'appearance' && <AppearanceTab />}
        </div>
      </div>
    </div>
  );
}
